import React, { useMemo, useState, JSX } from 'react'

import clsx from 'clsx'
import {
	FirstPage,
	LastPage,
	ChevronLeft,
	ChevronRight,
} from '@mui/icons-material'
import RDataTable, {
	TableProps,
	TableStyles,
} from 'react-data-table-component'
import Box from '@mui/material/Box'
import { useIntl } from 'react-intl'
import Menu from '@mui/material/Menu'
import Paper from '@mui/material/Paper'
import MenuItem from '@mui/material/MenuItem'
import { useTheme } from '@mui/material/styles'
import { IconButton, Tooltip } from '@mui/material'
import CancelIcon from '@mui/icons-material/Cancel'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import ArrowDownIcon from '@mui/icons-material/ArrowDropDown'

import RefreshGIF from '@/assets/lordicons/RefreshAnimated.gif'
import RefreshDarkPNG from '@/assets/lordicons/RefreshDark.png'
import RefreshDarkGIF from '@/assets/lordicons/RefreshAnimatedDark.gif'
import RefreshPNG from '@/assets/lordicons/Refresh.png'

import Heading from '../Heading'
import { Button } from '../Button'
import SubHeader from './SubHeader'
import { Loading } from '../Loading'
import { getTheme } from '../../config'
import { useWindowSize } from '../../hooks'
import TextField from '../formControls/TextField'
import { isDarkMode } from '../../services/helper'
import { ExportTable } from '../export/ExportTable'
import { tableRowsPerPageOptions } from '../../services/utils'

const theme = getTheme()

const bgColor = {
	'&:hover': {
		backgroundColor: 'transparent',
	},
}

type ActionItemBase<T> = {
	onClick: (_e: React.MouseEvent, _row: T) => Promise<void> | void
	hideIf?: (_e: T) => boolean
}

export type ActionItemWithComponent<T> = ActionItemBase<T> & {
	nameComponent: JSX.Element
}

export type ActionItemWithString<T> = ActionItemBase<T> & {
	name: string
}

export type ActionItem<T> =
	| ActionItemWithComponent<T>
	| ActionItemWithString<T>

function isActionItemWithComponent<T>(
	item: ActionItem<T>
): item is ActionItemWithComponent<T> {
	return 'nameComponent' in item && !!item.nameComponent
}

export type DataTableProps<T> = {
	titleActions?: React.ReactNode
	onRefreshClick?: () => void
	addNewBtn?: {
		text?: string
		onClick?: () => void
		disabled?: boolean
	}
	exportEnabled?: boolean
	exportData?: T[]
	onSearchChange?: (_e: React.ChangeEvent<HTMLInputElement>) => void
	searchText?: string
	onClearSearchClick?: () => void
	searchFilters?: React.ReactNode
	actionItems?: ActionItem<T>[]
	refreshing?: boolean
	selectedRows?: T[]
} & TableProps<T>

const DataTable = <T,>({
	title,
	titleActions,
	onRefreshClick,
	progressComponent = <Loading size="small" />,
	progressPending,
	addNewBtn,
	exportEnabled = false,
	exportData,
	data,
	onSearchChange,
	searchText,
	onClearSearchClick,
	searchFilters,
	columns,
	actionItems = [],
	expandableRows,
	expandableRowsComponent,
	refreshing = false,
	subHeader,
	subHeaderAlign,
	subHeaderWrap,
	...rest
}: DataTableProps<T>) => {
	const { dataTable: breakpoint } = theme.breakpoints
	const themeMUI = useTheme()
	const [width] = useWindowSize()
	const { formatMessage } = useIntl()

	const [actionAnchorEl, setActionAnchorEl] =
		useState<null | HTMLElement>(null)
	const [selectedActionItem, setSelectedActionItem] =
		useState<T | null>(null)

	const showActionMenu = (e: React.MouseEvent, row: T) => {
		e.stopPropagation()
		setActionAnchorEl(e.currentTarget as HTMLElement)
		setSelectedActionItem(row)
	}

	const customStyles: TableStyles = {
		responsiveWrapper: {
			style: {
				maxHeight: 'fit-content',
			},
		},
		headCells: {
			style: {
				backgroundColor: isDarkMode()
					? theme.color.primary.black
					: theme.color.primary.white,
			},
		},
		progress: {
			style: {
				backgroundColor: isDarkMode()
					? theme.color.primary.black
					: undefined,
			},
		},
		noData: {
			style: {
				backgroundColor: isDarkMode()
					? theme.color.primary.black
					: undefined,
				color: isDarkMode() ? theme.color.primary.white : undefined,
			},
		},
		pagination: {
			style: {
				'& button[disabled]': {
					opacity: isDarkMode() ? 0.3 : 0.5,
					cursor: 'not-allowed',
				},
			},
		},
	}

	const getPaginationIcon = (IconComponent: React.ElementType) => (
		<IconComponent
			style={{
				color: isDarkMode() && theme.color.primary.white,
			}}
		/>
	)

	const hideActionMenu = () => {
		setActionAnchorEl(null)
		setTimeout(() => setSelectedActionItem(null), 100)
	}

	const onActionItemClick = (
		e: React.MouseEvent,
		item: ActionItem<T>
	) => {
		if (selectedActionItem) item.onClick(e, selectedActionItem)
		hideActionMenu()
	}

	const expanderIconStyles = isDarkMode()
		? { color: 'white' }
		: undefined

	const filteredActionItems = useMemo(() => {
		return actionItems.filter((item) => {
			if (!item.hideIf) return true
			if (!selectedActionItem) return true
			return !item.hideIf(selectedActionItem)
		})
	}, [actionItems, selectedActionItem])

	return (
		<Box
			sx={{ width: '100%' }}
			className={
				rest.onRowClicked ? 'rdt_TableRow_Clickable' : undefined
			}
		>
			<header>
				<div
					className={clsx(
						'flex items-center mb-2',
						!title && !titleActions
							? 'justify-end'
							: 'justify-between'
					)}
				>
					{title && (
						<div className="flex flex-row items-center flex-shrink-0">
							<Heading
								button={
									typeof onRefreshClick === 'function' ? (
										<IconButton
											size="large"
											edge="start"
											color="inherit"
											aria-label="refresh"
											onClick={onRefreshClick}
											disabled={progressPending}
											className="ms-10"
											sx={bgColor}
										>
											<Tooltip title="Refresh table data">
												<img
													className="w-14 mt-1"
													src={
														themeMUI.palette.mode === 'light'
															? RefreshPNG
															: RefreshDarkPNG
													}
													onMouseOver={(e) => {
														e.currentTarget.src =
															themeMUI.palette.mode === 'light'
																? RefreshGIF
																: RefreshDarkGIF
													}}
													onFocus={(e) => {
														e.currentTarget.src =
															themeMUI.palette.mode === 'light'
																? RefreshGIF
																: RefreshDarkGIF
													}}
													onMouseOut={(e) => {
														e.currentTarget.src =
															themeMUI.palette.mode === 'light'
																? RefreshPNG
																: RefreshDarkPNG
													}}
													onBlur={(e) => {
														e.currentTarget.src =
															themeMUI.palette.mode === 'light'
																? RefreshPNG
																: RefreshDarkPNG
													}}
													alt="refresh"
												/>
											</Tooltip>
										</IconButton>
									) : null
								}
							>
								{title}
							</Heading>
						</div>
					)}

					<div className="flex justify-end gap-2">
						{exportEnabled &&
							((exportData || []).length > 0 || data.length > 0) && (
								<ExportTable
									data={exportData || data}
									title={title}
								/>
							)}
						{titleActions}
						{addNewBtn && (
							// @ts-ignore
							<>
								{addNewBtn.onClick && (
									<Button
										text={
											addNewBtn.text ||
											formatMessage({ id: 'global.add_new' })
										}
										onClick={addNewBtn.onClick}
										disabled={addNewBtn.disabled}
									/>
								)}
								{!addNewBtn.onClick && addNewBtn}
							</>
						)}
					</div>
				</div>
			</header>
			{subHeader && (
				<SubHeader wrapContent={subHeaderWrap} align={subHeaderAlign}>
					{subHeader}
				</SubHeader>
			)}

			{(onSearchChange || searchFilters) && (
				<div className="mb-3 gap-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
					{onSearchChange && (
						<TextField
							label="Search..."
							onChange={onSearchChange}
							value={searchText}
							InputProps={
								searchText && onClearSearchClick
									? {
											endAdornment: (
												<div className="pr-2">
													<IconButton
														edge="end"
														onClick={onClearSearchClick}
														size="small"
													>
														<CancelIcon fontSize="small" />
													</IconButton>
												</div>
											),
										}
									: undefined
							}
						/>
					)}
					{searchFilters}
				</div>
			)}
			<Paper sx={{ pt: 2, mb: 2 }}>
				{refreshing && !progressPending && (
					<div className="fixed z-100 w-full">
						<Loading size="small" />
					</div>
				)}
				<RDataTable
					customStyles={customStyles}
					progressComponent={
						progressComponent || <Loading size="small" />
					}
					progressPending={progressPending}
					data={data}
					expandableRows={
						expandableRowsComponent && expandableRows === undefined
							? width < breakpoint
							: expandableRows
					}
					expandableRowsComponent={expandableRowsComponent}
					columns={
						actionItems && actionItems.length > 0
							? [
									...columns,
									{
										name: '',
										cell: (row) => {
											const isOneItemVisible = actionItems.find(
												(item) =>
													typeof item.hideIf !== 'function' ||
													!item.hideIf(row)
											)
											if (isOneItemVisible) {
												return (
													<IconButton
														size="small"
														edge="start"
														color="inherit"
														aria-label="refresh"
														onClick={(e) => showActionMenu(e, row)}
														sx={bgColor}
													>
														<MoreVertIcon />
													</IconButton>
												)
											}
											return ''
										},
										width: '60px',
									},
								]
							: columns
					}
					paginationPerPage={tableRowsPerPageOptions[0]}
					paginationRowsPerPageOptions={tableRowsPerPageOptions}
					expandableIcon={{
						expanded: <ArrowDownIcon style={expanderIconStyles} />,
						collapsed: <ArrowRightIcon style={expanderIconStyles} />,
					}}
					paginationIconFirstPage={getPaginationIcon(FirstPage)}
					paginationIconLastPage={getPaginationIcon(LastPage)}
					paginationIconPrevious={getPaginationIcon(ChevronLeft)}
					paginationIconNext={getPaginationIcon(ChevronRight)}
					{...rest}
				/>
			</Paper>
			{actionItems && (
				<Menu
					id="menu-appbar"
					anchorEl={actionAnchorEl}
					anchorOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
					keepMounted
					transformOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
					open={Boolean(actionAnchorEl)}
					className="mt-8 sm:mt-10"
					onClose={hideActionMenu}
				>
					{filteredActionItems.map((item, index) => {
						let content: React.ReactNode = null

						content = isActionItemWithComponent(item)
							? item.nameComponent
							: item.name

						return (
							<MenuItem
								key={index}
								onClick={(e) => onActionItemClick(e, item)}
							>
								{content}
							</MenuItem>
						)
					})}
				</Menu>
			)}
		</Box>
	)
}

export default DataTable
